import {
  useQuery,
  useMutation,
  useQueryClient,
  keepPreviousData,
} from "@tanstack/react-query";
import { appointmentsAPI } from "@/lib/appointments-api";
import { queryKeys, invalidateQueries } from "@/lib/query-keys";
import type {
  InvestigationListParams,
  CreateInvestigationNoteRequest,
} from "@/types/appointments";

export const useInvestigationSearch = (params?: InvestigationListParams) => {
  return useQuery({
    queryKey: queryKeys.consultation.catalogs.investigations(
      params as Record<string, unknown>
    ),
    queryFn: () => appointmentsAPI.getInvestigations(params),
    enabled: !!params?.search || !!params?.type, // Only fetch if search or type is provided, or if we want to list all (maybe optional)
    staleTime: 5 * 60 * 1000, // 5 minutes
    placeholderData: keepPreviousData,
  });
};

export const usePatientInvestigationNotes = (
  appointmentId: string,
  consolidated: boolean = false
) => {
  return useQuery({
    queryKey: queryKeys.consultation.patientData.investigationNotes(
      appointmentId,
      consolidated
    ),
    queryFn: () =>
      appointmentsAPI.getPatientInvestigationNotes(appointmentId, consolidated),
    enabled: !!appointmentId,
    select: (data) => data,
  });
};

export const useCreateInvestigationNote = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      appointmentId,
      data,
    }: {
      appointmentId: string;
      data: CreateInvestigationNoteRequest;
    }) => appointmentsAPI.createPatientInvestigationNote(appointmentId, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries(
        invalidateQueries.patientInvestigationNotes(
          variables.appointmentId,
          false
        )
      );
      queryClient.invalidateQueries(
        invalidateQueries.patientInvestigationNotes(
          variables.appointmentId,
          true
        )
      );
      // Also invalidate search to update the exclusion list if needed
      queryClient.invalidateQueries({
        queryKey: ["consultation", "catalogs", "investigations"],
      });
    },
  });
};

export const useUpdateInvestigationNote = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      appointmentId,
      noteId,
      data,
    }: {
      appointmentId: string;
      noteId: string;
      data: { value: number };
    }) =>
      appointmentsAPI.updatePatientInvestigationNote(
        appointmentId,
        noteId,
        data
      ),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries(
        invalidateQueries.patientInvestigationNotes(
          variables.appointmentId,
          false
        )
      );
      queryClient.invalidateQueries(
        invalidateQueries.patientInvestigationNotes(
          variables.appointmentId,
          true
        )
      );
    },
  });
};

export const useDeleteInvestigationNote = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      appointmentId,
      noteId,
    }: {
      appointmentId: string;
      noteId: string;
    }) => appointmentsAPI.deletePatientInvestigationNote(appointmentId, noteId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries(
        invalidateQueries.patientInvestigationNotes(
          variables.appointmentId,
          false
        )
      );
      queryClient.invalidateQueries(
        invalidateQueries.patientInvestigationNotes(
          variables.appointmentId,
          true
        )
      );
      // Also invalidate search to update the exclusion list if needed
      queryClient.invalidateQueries({
        queryKey: ["consultation", "catalogs", "investigations"],
      });
    },
  });
};
